import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, Chip, Link } from "@mui/material";
import moment from "moment-timezone";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { GetDepartureBatchList } from "api/departureBatch";
import useStationRefresh from "hooks/useStationRefresh";
import BatchDetailDialog from "./BatchDetailDialog";

const STATUS_KEY = {
  departed: "statusDeparted",
  partially_cancelled: "statusPartiallyCancelled",
  cancelled: "statusCancelled",
};

// Batch-level status is informational, not a warning the user needs to act
// on — per this repo's color convention, that's `color="secondary"`, not
// `color="error"`, for all three values.
const HistoryTab = ({ onItemsChanged }) => {
  const { t } = useTranslation();
  const { user: _user } = useStationRefresh();
  const stationList = _user?.stations || [];

  const [selectedStationId, setSelectedStationId] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openBatchId, setOpenBatchId] = useState(null);

  const refreshList = () => {
    setIsLoading(true);
    GetDepartureBatchList(selectedStationId || undefined).then((res) => {
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStationId]);

  const handleDialogChanged = () => {
    refreshList();
    onItemsChanged?.();
  };

  const columns = [
    { field: "batchNo", headerName: t("departureBatch.historyColumnBatchNo"), flex: 1 },
    {
      field: "route",
      headerName: t("departureBatch.historyColumnRoute"),
      flex: 1,
      valueGetter: (params) => `${params.row.fromStationId} → ${params.row.toStationId}`,
    },
    {
      field: "items",
      headerName: t("departureBatch.historyColumnItems"),
      flex: 0.6,
      valueGetter: (params) => params.row.items?.length || 0,
    },
    {
      field: "active",
      headerName: t("departureBatch.historyColumnActive"),
      flex: 0.6,
      valueGetter: (params) => (params.row.items || []).filter((i) => !i.removedAt).length,
    },
    {
      field: "status",
      headerName: t("departureBatch.historyColumnStatus"),
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={t(`departureBatch.${STATUS_KEY[params.value] || "statusDeparted"}`)}
          size="small"
          color="secondary"
          variant={params.value === "departed" ? "filled" : "outlined"}
        />
      ),
    },
    { field: "submittedBy", headerName: t("departureBatch.historyColumnSubmittedBy"), flex: 1 },
    {
      field: "createdAt",
      headerName: t("departureBatch.historyColumnCreatedAt"),
      flex: 1,
      valueGetter: (params) =>
        params.row.createdAt
          ? moment(params.row.createdAt).tz("Asia/Vientiane").format("DD/MM/YYYY, h:mm:ss A")
          : "",
    },
    {
      field: "view",
      headerName: "",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Link component="button" color="secondary" underline="hover" onClick={() => setOpenBatchId(params.row._id)}>
          {t("departureBatch.detailTitle")}
        </Link>
      ),
    },
  ];

  return (
    <>
      <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel shrink>{t("departureBatch.selectStation")}</InputLabel>
          <Select
            label={t("departureBatch.selectStation")}
            value={selectedStationId}
            onChange={(e) => setSelectedStationId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">{t("departureBatch.allStations")}</MenuItem>
            {stationList.map(({ id, name }) => (
              <MenuItem key={id} value={id}>
                {id} ({name})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </BentoCard>

      {!isLoading && rows.length === 0 ? (
        <BentoCard bodySx={{ p: "48px 22px", textAlign: "center" }}>
          <LocalShippingOutlinedIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
          <Typography color="text.secondary">{t("departureBatch.noBatches")}</Typography>
        </BentoCard>
      ) : (
        <Box height="70vh" width="100%">
          <StyledDataGrid
            loading={isLoading}
            getRowId={(row) => row._id}
            rows={rows}
            columns={columns}
            onRowClick={(params) => setOpenBatchId(params.row._id)}
            containerSx={{ height: "100%" }}
            autoHeight={false}
          />
        </Box>
      )}

      <BatchDetailDialog
        open={!!openBatchId}
        batchId={openBatchId}
        onClose={() => setOpenBatchId(null)}
        onChanged={handleDialogChanged}
      />
    </>
  );
};

export default HistoryTab;
